/* Corazon: local snapshot store for personalized products (contract: docs/PERSONALIZATION_CONTRACT.md).
   A builder calls saveLocalPreview(designId, image) at add to cart; the cart drawer and /cart page call
   getLocalPreview(designId) to show the customer's own design as the line thumbnail.
   This is the stopgap until the upload backend writes an https `_preview` URL onto the line. It only lives in this
   browser (localStorage), so the cart falls back to the product image anywhere else.

   window.CGCPreviewStore.saveLocalPreview(designId, image[, opts]) -> Promise<dataUrl|null>
       image: a data URL, a Blob/File, an <img>, a <canvas> or an image URL (same-origin or CORS-enabled).
       Shrinks it to ~600 px on the long side, JPEG quality 0.8, on a white background (no alpha in JPEG),
       stores it under localStorage['cgc_preview:' + designId] and prunes old entries.
   window.CGCPreviewStore.getLocalPreview(designId) -> dataUrl string or null
   window.CGCPreviewStore.removeLocalPreview(designId)
   The two main functions are also exposed as window.saveLocalPreview / window.getLocalPreview. */
(function () {
  if (window.CGCPreviewStore) return;
  var PREFIX = 'cgc_preview:';
  var INDEX = 'cgc_preview_index';          /* { designId: savedAtMs } */
  var MAX_ENTRIES = 12;
  var MAX_AGE_MS = 45 * 24 * 3600 * 1000;   /* a cart rarely lives longer than this */
  var MAX_PX = 600, QUALITY = 0.8;

  function ls() { try { return window.localStorage; } catch (e) { return null; } }
  function readIndex() { var s = ls(); if (!s) return {}; try { return JSON.parse(s.getItem(INDEX) || '{}') || {}; } catch (e) { return {}; } }
  function writeIndex(ix) { var s = ls(); if (s) try { s.setItem(INDEX, JSON.stringify(ix)); } catch (e) {} }
  function clean(id) { return String(id == null ? '' : id).trim(); }

  /* drop expired entries, entries whose data is gone, and the oldest beyond MAX_ENTRIES (keep = id never dropped) */
  function prune(keep, maxEntries) {
    var s = ls(); if (!s) return;
    var ix = readIndex(), now = Date.now(), max = maxEntries == null ? MAX_ENTRIES : maxEntries;
    Object.keys(ix).forEach(function (id) {
      if (id !== keep && (now - (+ix[id] || 0) > MAX_AGE_MS || s.getItem(PREFIX + id) == null)) { s.removeItem(PREFIX + id); delete ix[id]; }
    });
    var ids = Object.keys(ix).filter(function (id) { return id !== keep; }).sort(function (a, b) { return ix[a] - ix[b]; });
    var total = ids.length + (keep && ix[keep] ? 1 : 0);
    while (total > max && ids.length) { var old = ids.shift(); s.removeItem(PREFIX + old); delete ix[old]; total--; }
    writeIndex(ix);
  }

  function loadImage(src) {
    return new Promise(function (res, rej) {
      var im = new Image();
      if (!/^data:|^blob:/.test(src)) im.crossOrigin = 'anonymous';
      im.onload = function () { res(im); }; im.onerror = function () { rej(new Error('preview image did not load')); };
      im.src = src;
    });
  }
  function toDrawable(image) {
    if (!image) return Promise.reject(new Error('no image'));
    if (typeof HTMLCanvasElement !== 'undefined' && image instanceof HTMLCanvasElement) return Promise.resolve(image);
    if (typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement) return image.complete && image.naturalWidth ? Promise.resolve(image) : loadImage(image.src);
    if (typeof Blob !== 'undefined' && image instanceof Blob) {
      var url = URL.createObjectURL(image);
      return loadImage(url).then(function (im) { setTimeout(function () { URL.revokeObjectURL(url); }, 0); return im; });
    }
    return loadImage(String(image));
  }
  function compress(image, maxPx, quality) {
    return toDrawable(image).then(function (src) {
      var w = src.naturalWidth || src.width, h = src.naturalHeight || src.height;
      if (!w || !h) throw new Error('empty preview image');
      var k = Math.min(1, (maxPx || MAX_PX) / Math.max(w, h));
      var c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
      var g = c.getContext('2d');
      g.fillStyle = '#ffffff'; g.fillRect(0, 0, c.width, c.height);   /* JPEG has no alpha: shapes sit on white */
      g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
      g.drawImage(src, 0, 0, c.width, c.height);
      return c.toDataURL('image/jpeg', quality || QUALITY);
    });
  }

  function store(id, dataUrl) {
    var s = ls(); if (!s) return false;
    for (;;) {
      try {
        s.setItem(PREFIX + id, dataUrl);
        var ix = readIndex(); ix[id] = Date.now(); writeIndex(ix);
        return true;
      } catch (e) {
        /* storage full: drop the oldest other preview and try again; give up when none are left */
        var others = readIndex(); delete others[id];
        var ids = Object.keys(others).sort(function (a, b) { return others[a] - others[b]; });
        if (!ids.length) return false;
        removeLocalPreview(ids[0]);
      }
    }
  }

  function saveLocalPreview(designId, image, opts) {
    var id = clean(designId);
    if (!id) return Promise.resolve(null);
    opts = opts || {};
    return compress(image, opts.maxPx, opts.quality).then(function (dataUrl) {
      if (!store(id, dataUrl)) return null;
      prune(id);
      try { document.dispatchEvent(new CustomEvent('cgc:preview-saved', { detail: { designId: id } })); } catch (e) {}
      return dataUrl;
    }).catch(function (e) { console.warn('[cgc-preview-store] not saved', e); return null; });
  }

  function getLocalPreview(designId) {
    var id = clean(designId), s = ls();
    if (!id || !s) return null;
    try { var v = s.getItem(PREFIX + id); return v && v.indexOf('data:image/') === 0 ? v : null; } catch (e) { return null; }
  }

  function removeLocalPreview(designId) {
    var id = clean(designId), s = ls(); if (!id || !s) return;
    try { s.removeItem(PREFIX + id); var ix = readIndex(); delete ix[id]; writeIndex(ix); } catch (e) {}
  }

  window.CGCPreviewStore = { saveLocalPreview: saveLocalPreview, getLocalPreview: getLocalPreview, removeLocalPreview: removeLocalPreview,
    prune: prune, KEY_PREFIX: PREFIX, MAX_PX: MAX_PX, QUALITY: QUALITY };
  if (!window.saveLocalPreview) window.saveLocalPreview = saveLocalPreview;
  if (!window.getLocalPreview) window.getLocalPreview = getLocalPreview;
})();
